import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  ArrowLeft,
  Mic,
  Save,
  Volume2,
  RotateCcw
} from "lucide-react";
import { toast } from "sonner";

export default function SpeechTestPage() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [patients, setPatients] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(patientId || "");
  const [selectedClinic, setSelectedClinic] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  
  // Speech test data
  const [srtLeft, setSrtLeft] = useState(null);
  const [srtRight, setSrtRight] = useState(null);
  const [wrsLeft, setWrsLeft] = useState(null);
  const [wrsRight, setWrsRight] = useState(null);

  useEffect(() => {
    fetchPatients();
    fetchClinics();
  }, []);

  useEffect(() => {
    if (patientId) {
      setSelectedPatient(patientId);
    }
  }, [patientId]);

  const fetchPatients = async () => {
    try {
      const response = await apiClient.get("/patients");
      setPatients(response.data);
    } catch (error) {
      console.error("Failed to fetch patients:", error);
    }
  };

  const fetchClinics = async () => {
    try {
      const response = await apiClient.get("/clinics");
      setClinics(response.data);
      if (response.data.length > 0) {
        setSelectedClinic(response.data[0].id);
      }
    } catch (error) {
      console.error("Failed to fetch clinics:", error);
    }
  };

  const handleSaveTest = async () => {
    if (!selectedPatient) {
      toast.error("Please select a patient");
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        patient_id: selectedPatient,
        clinic_id: selectedClinic || clinics[0]?.id,
        srt_left: srtLeft,
        srt_right: srtRight,
        wrs_left: wrsLeft,
        wrs_right: wrsRight,
        notes: notes || null
      };
      
      await apiClient.post("/speech-tests", payload);
      toast.success("Speech Test saved successfully!");
      navigate(`/patients/${selectedPatient}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save test");
    } finally {
      setSaving(false);
    }
  };

  const clearAll = () => {
    setSrtLeft(null);
    setSrtRight(null);
    setWrsLeft(null);
    setWrsRight(null);
    setNotes("");
    toast.info("All values cleared");
  };

  const getWRSInterpretation = (score) => {
    if (score === null) return "Not tested";
    if (score >= 90) return "Excellent";
    if (score >= 80) return "Good";
    if (score >= 70) return "Fair";
    if (score >= 50) return "Poor";
    return "Very Poor";
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="speech-test-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">
                Speech Audiometry
              </h1>
              <p className="text-slate-500">Record SRT and Word Recognition Scores</p>
            </div>
          </div>
          <Button 
            className="bg-sky-600 hover:bg-sky-700"
            onClick={handleSaveTest}
            disabled={saving}
            data-testid="save-speech-test-btn"
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                Saving...
              </span>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Test
              </>
            )}
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Patient Selection */}
          <Card>
            <CardHeader>
              <CardTitle>Test Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Patient *</Label>
                <Select value={selectedPatient} onValueChange={setSelectedPatient}>
                  <SelectTrigger data-testid="speech-patient-select">
                    <SelectValue placeholder="Select patient" />
                  </SelectTrigger>
                  <SelectContent>
                    {patients.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} ({p.age} yrs)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="pt-4">
                <Button variant="outline" className="w-full" onClick={clearAll}>
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Clear All Values
                </Button>
              </div>

              <div>
                <Label>Clinical Notes</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add observations about patient response, masking used, etc."
                  className="mt-2"
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>

          {/* SRT Testing */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mic className="w-5 h-5 text-sky-600" />
                Speech Recognition Threshold (SRT)
              </CardTitle>
              <CardDescription>
                Lowest level at which patient can repeat 50% of spondaic words
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Right Ear SRT */}
              <div className="p-4 bg-red-50 rounded-lg">
                <Label className="text-red-800 font-semibold">Right Ear (O)</Label>
                <div className="mt-3">
                  <Input
                    type="number"
                    value={srtRight ?? ""}
                    onChange={(e) => setSrtRight(e.target.value ? parseFloat(e.target.value) : null)}
                    placeholder="dB HL"
                    className="text-lg"
                    min={-10}
                    max={120}
                    step={5}
                  />
                  <Slider
                    value={[srtRight ?? 25]}
                    onValueChange={(v) => setSrtRight(v[0])}
                    min={-10}
                    max={120}
                    step={5}
                    className="mt-3"
                  />
                  <p className="text-sm text-red-600 mt-2">
                    {srtRight !== null ? `${srtRight} dB HL` : "Not recorded"}
                  </p>
                </div>
              </div>

              {/* Left Ear SRT */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <Label className="text-blue-800 font-semibold">Left Ear (X)</Label>
                <div className="mt-3">
                  <Input
                    type="number"
                    value={srtLeft ?? ""}
                    onChange={(e) => setSrtLeft(e.target.value ? parseFloat(e.target.value) : null)}
                    placeholder="dB HL"
                    className="text-lg"
                    min={-10}
                    max={120}
                    step={5}
                  />
                  <Slider
                    value={[srtLeft ?? 25]}
                    onValueChange={(v) => setSrtLeft(v[0])}
                    min={-10}
                    max={120}
                    step={5}
                    className="mt-3"
                  />
                  <p className="text-sm text-blue-600 mt-2">
                    {srtLeft !== null ? `${srtLeft} dB HL` : "Not recorded"}
                  </p>
                </div>
              </div>

              <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
                <p className="font-medium">Normal SRT: 0-25 dB HL</p>
                <p>SRT should correlate with PTA (±10 dB)</p>
              </div>
            </CardContent>
          </Card>

          {/* WRS Testing */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Volume2 className="w-5 h-5 text-emerald-600" />
                Word Recognition Score (WRS/SDS)
              </CardTitle>
              <CardDescription>
                Percentage of monosyllabic words correctly repeated at suprathreshold
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Right Ear WRS */}
              <div className="p-4 bg-red-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <Label className="text-red-800 font-semibold">Right Ear (O)</Label>
                  <span className="text-sm font-medium text-red-700">
                    {getWRSInterpretation(wrsRight)}
                  </span>
                </div>
                <div className="mt-3">
                  <Input
                    type="number"
                    value={wrsRight ?? ""}
                    onChange={(e) => setWrsRight(e.target.value ? parseFloat(e.target.value) : null)}
                    placeholder="% correct"
                    className="text-lg"
                    min={0}
                    max={100}
                    step={4}
                  />
                  <Slider
                    value={[wrsRight ?? 100]}
                    onValueChange={(v) => setWrsRight(v[0])}
                    min={0}
                    max={100}
                    step={4}
                    className="mt-3"
                  />
                  <p className="text-sm text-red-600 mt-2">
                    {wrsRight !== null ? `${wrsRight}%` : "Not recorded"}
                  </p>
                </div>
              </div>

              {/* Left Ear WRS */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <Label className="text-blue-800 font-semibold">Left Ear (X)</Label>
                  <span className="text-sm font-medium text-blue-700">
                    {getWRSInterpretation(wrsLeft)}
                  </span>
                </div>
                <div className="mt-3">
                  <Input
                    type="number"
                    value={wrsLeft ?? ""}
                    onChange={(e) => setWrsLeft(e.target.value ? parseFloat(e.target.value) : null)}
                    placeholder="% correct"
                    className="text-lg"
                    min={0}
                    max={100}
                    step={4}
                  />
                  <Slider
                    value={[wrsLeft ?? 100]}
                    onValueChange={(v) => setWrsLeft(v[0])}
                    min={0}
                    max={100}
                    step={4}
                    className="mt-3"
                  />
                  <p className="text-sm text-blue-600 mt-2">
                    {wrsLeft !== null ? `${wrsLeft}%` : "Not recorded"}
                  </p>
                </div>
              </div>

              <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
                <p className="font-medium mb-1">WRS Interpretation:</p>
                <ul className="space-y-1 text-xs">
                  <li>90-100%: Excellent - Good candidate for amplification</li>
                  <li>80-89%: Good - Will benefit from hearing aids</li>
                  <li>70-79%: Fair - May need additional support</li>
                  <li>50-69%: Poor - Consider advanced features</li>
                  <li>&lt;50%: Very Poor - May need cochlear implant evaluation</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Summary Card */}
        {(srtLeft !== null || srtRight !== null || wrsLeft !== null || wrsRight !== null) && (
          <Card className="bg-gradient-to-r from-sky-50 to-blue-50">
            <CardHeader>
              <CardTitle>Test Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-white rounded-lg">
                  <p className="text-sm text-slate-500">SRT Right</p>
                  <p className="text-2xl font-bold text-red-600">
                    {srtRight !== null ? `${srtRight} dB` : "-"}
                  </p>
                </div>
                <div className="p-4 bg-white rounded-lg">
                  <p className="text-sm text-slate-500">SRT Left</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {srtLeft !== null ? `${srtLeft} dB` : "-"}
                  </p>
                </div>
                <div className="p-4 bg-white rounded-lg">
                  <p className="text-sm text-slate-500">WRS Right</p>
                  <p className="text-2xl font-bold text-red-600">
                    {wrsRight !== null ? `${wrsRight}%` : "-"}
                  </p>
                </div>
                <div className="p-4 bg-white rounded-lg">
                  <p className="text-sm text-slate-500">WRS Left</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {wrsLeft !== null ? `${wrsLeft}%` : "-"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
